import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	inject,
	NgZone,
	OnChanges,
	OnDestroy,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { environment } from '@environment/environment';
import { CurrentUserService } from '@services/auth/current-user.service';
import { LucideModule } from '@shared/modules/lucide.module';
import { ImageLoadQueueService } from '@shared/services/image-load-queue.service';
import { ScrollVisibilityService } from '@shared/services/scroll-visibility.service';
import { SharedModule } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

/**
 * Interface to store cache data with timestamp
 */
interface CacheEntry {
	url: string | null;
	timestamp: number;
}

@Component({
	selector: 'app-avatar',
	imports: [SharedModule, ImageModule, TooltipModule, LucideModule],
	templateUrl: './avatar.component.html',
	styleUrls: ['./avatar.component.scss'],
})
export class AvatarComponent implements AfterViewInit, OnChanges, OnDestroy {
	@Input() imageUrl?: string;
	@Input() name!: string;
	@Input() extraInfo?: string;
	@Input() size = 48; // Size of the avatar in pixels (default: 48px)
	@Input() showInfo = true; // Show name and extra info next to avatar
	@Input() userId?: number; // Optional: if provided, will fetch profile photo from Supabase
	@Input() lazyLoad = true; // Enable lazy loading by default for better performance
	@Input() showTooltip = true; // Show tooltip on hover with the name (enabled by default)
	@Input() enablePreview = true; // Enable image preview/zoom on click (enabled by default)
	@ViewChild('avatarContainer', { static: false }) avatarContainer!: ElementRef<HTMLElement>;

	fullImageUrl?: string;
	originalImageUrl?: string; // Original image URL without transformations for preview
	private http = inject(HttpClient);
	private platformId = inject(PLATFORM_ID);
	private ngZone = inject(NgZone);
	private cdr = inject(ChangeDetectorRef);
	private imageLoadQueue = inject(ImageLoadQueueService);
	private scrollVisibilityService = inject(ScrollVisibilityService);
	private currentUserService = inject(CurrentUserService);
	private intersectionObserver?: IntersectionObserver;
	private scrollSubscription?: Subscription;
	private resizeSubscription?: Subscription;
	private imageLoaded = false;
	private loadRequested = false;

	// ============================================
	// Static Cache System
	// ============================================
	/**
	 * Static cache of profile photo URLs
	 * Key: userId, Value: { url, timestamp }
	 */
	private static photoCache = new Map<number, CacheEntry>();

	/**
	 * Static cache of original photo URLs (for modal)
	 * Key: userId, Value: { url, timestamp }
	 */
	private static originalPhotoCache = new Map<number, CacheEntry>();

	/**
	 * Map of in-progress HTTP requests to avoid duplicates
	 * Key: userId, Value: Observable in progress
	 */
	private static pendingRequests = new Map<number, Observable<{ url: string | null }>>();

	/**
	 * Map of in-progress HTTP requests for original photos
	 * Key: userId, Value: Observable in progress
	 */
	private static pendingOriginalRequests = new Map<number, Observable<{ url: string | null }>>();

	/**
	 * Cache expiration time in milliseconds (default: 30 minutes)
	 */
	private static CACHE_EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes

	/**
	 * Load original image without transformations for preview (with cache)
	 */
	private loadOriginalImage(): void {
		const id = this.userId;
		if (!id) return;

		const numericId = Number(id);
		if (Number.isNaN(numericId)) {
			console.warn(`⚠️ Avatar: invalid userId in loadOriginalImage: ${id}`);
			return;
		}

		// For now, use the same image as fullImageUrl (no separate original)
		// In the future, we could fetch a higher quality version if needed
		this.originalImageUrl = this.fullImageUrl;
		this.cdr.markForCheck();
	}

	/**
	 * Public static method to clear cache for a specific user
	 * Useful when updating a user's profile photo
	 * @param userId ID of the user whose cache should be cleared
	 */
	public static clearCache(userId: number): void {
		AvatarComponent.photoCache.delete(userId);
		AvatarComponent.originalPhotoCache.delete(userId);
		AvatarComponent.pendingRequests.delete(userId);
		AvatarComponent.pendingOriginalRequests.delete(userId);
	}

	/**
	 * Public static method to clear all cache
	 * Useful to force reload of all images
	 */
	public static clearAllCache(): void {
		AvatarComponent.photoCache.clear();
		AvatarComponent.originalPhotoCache.clear();
		AvatarComponent.pendingRequests.clear();
		AvatarComponent.pendingOriginalRequests.clear();
	}

	/**
	 * Public static method to get cache statistics
	 */
	public static getCacheStats(): {
		photoCacheSize: number;
		originalPhotoCacheSize: number;
		pendingRequestsCount: number;
		pendingOriginalRequestsCount: number;
	} {
		return {
			photoCacheSize: AvatarComponent.photoCache.size,
			originalPhotoCacheSize: AvatarComponent.originalPhotoCache.size,
			pendingRequestsCount: AvatarComponent.pendingRequests.size,
			pendingOriginalRequestsCount: AvatarComponent.pendingOriginalRequests.size,
		};
	}

	ngOnChanges(changes: SimpleChanges): void {
		// If userId or imageUrl changes, reset state to allow reload
		if (changes['userId'] || changes['imageUrl']) {
			// Only reset if value actually changed (not on first load)
			const userIdChanged =
				changes['userId'] &&
				!changes['userId'].firstChange &&
				changes['userId'].previousValue !== changes['userId'].currentValue;
			const imageUrlChanged =
				changes['imageUrl'] &&
				!changes['imageUrl'].firstChange &&
				changes['imageUrl'].previousValue !== changes['imageUrl'].currentValue;

			if (userIdChanged || imageUrlChanged) {
				this.imageLoaded = false;
				this.loadRequested = false;
				this.fullImageUrl = undefined;
				this.originalImageUrl = undefined;
				// Clear previous observers
				this.cleanup();

				// If component is initialized, reload image
				if (isPlatformBrowser(this.platformId) && this.avatarContainer?.nativeElement) {
					if (this.lazyLoad) {
						// Restart lazy loading process
						this.initWithMultipleStrategies();
					} else {
						// Load immediately if lazy loading is disabled
						this.loadImage();
					}
				}
			}
		}
	}

	ngAfterViewInit() {
		if (!isPlatformBrowser(this.platformId)) {
			if (!this.lazyLoad) {
				this.loadImage();
			}
			return;
		}

		// If there is no userId or imageUrl, there is nothing to load - show initials immediately
		const hasImageSource = this.userId || this.imageUrl;
		if (!hasImageSource) {
			this.fullImageUrl = undefined;
			this.imageLoaded = true;
			this.cdr.markForCheck();
			return;
		}

		// For lazy loading, we need to wait for DOM and check visibility
		if (this.lazyLoad) {
			// Use multiple strategies to ensure we catch the element
			this.initWithMultipleStrategies();
		} else {
			// If lazy loading is disabled, load immediately but after current cycle
			// to avoid ExpressionChangedAfterItHasBeenCheckedError
			setTimeout(() => {
				this.loadImage();
			}, 0);
		}
	}

	private initWithMultipleStrategies(): void {
		// Single strategy: immediate load with requestAnimationFrame
		this.ngZone.runOutsideAngular(() => {
			requestAnimationFrame(() => {
				this.tryLoadImage();
			});
		});
	}

	private tryLoadImage(): void {
		if (this.loadRequested) return;

		const checkElement = () => {
			if (this.loadRequested) return;

			if (!this.avatarContainer?.nativeElement) {
				// If element not found, load anyway
				this.requestImageLoad();
				return;
			}

			const element = this.avatarContainer.nativeElement;

			// Check visibility: if visible load, otherwise setup observer
			if (this.checkVisibility(element)) {
				this.requestImageLoad();
			} else {
				// Set up lazy loading only if not visible
				this.setupIntersectionObserver(element);
				this.setupEventListeners(element);
			}
		};

		checkElement();
	}

	private checkVisibility(element: HTMLElement): boolean {
		try {
			// Fast check: if element is not in DOM, skip expensive operations
			if (!element.offsetParent && element.offsetWidth === 0 && element.offsetHeight === 0) {
				return false;
			}

			const rect = element.getBoundingClientRect();
			const windowHeight = window.innerHeight || document.documentElement.clientHeight;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth;

			// Check visibility with optimized margin
			const isInViewport =
				rect.top < windowHeight + 300 &&
				rect.bottom > -300 &&
				rect.left < windowWidth + 300 &&
				rect.right > -300;

			// Verify element has valid dimensions
			const hasDimensions = rect.width >= 0 && rect.height >= 0;

			// Check computed style only when potentially visible
			if (!isInViewport || !hasDimensions) {
				return false;
			}

			// Check computed style after viewport validation
			const style = window.getComputedStyle(element);
			const isDisplayed =
				style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0;

			return isDisplayed;
		} catch (_error) {
			// If check fails, assume visible to be safe - better to load than miss
			return true;
		}
	}

	private setupIntersectionObserver(element: HTMLElement): void {
		if (this.loadRequested) return;

		try {
			this.intersectionObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting && !this.loadRequested) {
							this.ngZone.run(() => {
								this.requestImageLoad();
							});
							this.cleanup();
						}
					});
				},
				{
					rootMargin: '200px',
					threshold: 0.01,
				},
			);

			this.intersectionObserver.observe(element);
		} catch (error) {
			console.error('Error setting up Intersection Observer:', error);
			// Fallback: check visibility and load if visible
			if (this.checkVisibility(element)) {
				this.requestImageLoad();
			}
		}
	}

	private setupEventListeners(element: HTMLElement): void {
		if (this.loadRequested) return;

		// Simplified: only use scroll events, no resize
		this.scrollSubscription = this.scrollVisibilityService.scroll$.subscribe(() => {
			if (this.loadRequested) return;
			// Run visibility check outside Angular zone
			this.ngZone.runOutsideAngular(() => {
				if (this.checkVisibility(element)) {
					this.ngZone.run(() => {
						this.requestImageLoad();
						this.cleanup();
					});
				}
			});
		});
	}

	private requestImageLoad(): void {
		if (this.loadRequested) return;
		this.loadRequested = true;

		// Enqueue load - no need to run in zone, the queue handles it
		this.imageLoadQueue.enqueueLoad(() => {
			this.loadImage();
			// Force change detection after loading
			this.cdr.markForCheck();
		});
	}

	private cleanup(): void {
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
			this.intersectionObserver = undefined;
		}
		if (this.scrollSubscription) {
			this.scrollSubscription.unsubscribe();
			this.scrollSubscription = undefined;
		}
		if (this.resizeSubscription) {
			this.resizeSubscription.unsubscribe();
			this.resizeSubscription = undefined;
		}
	}

	ngOnDestroy() {
		this.cleanup();
	}

	/**
	 * Checks if a cache entry is valid (not expired)
	 */
	private static isCacheValid(entry: CacheEntry): boolean {
		return Date.now() - entry.timestamp < AvatarComponent.CACHE_EXPIRATION_TIME;
	}

	/**
	 * Gets the photo URL from cache or makes an HTTP request
	 */
	private loadImage(): void {
		if (this.imageLoaded) return;
		this.imageLoaded = true;

		// Use userId to fetch profile photo
		const id = this.userId;

		// If userId exists, fetch URL from backend (with cache)
		if (id) {
			const numericId = Number(id);
			if (Number.isNaN(numericId)) {
				console.warn(`⚠️ Avatar: invalid userId: ${id} (type: ${typeof id})`);
				return;
			}

			// 0. Check if this is the current user - use cached photo from login
			const currentUserId = this.currentUserService.getUserId();
			if (currentUserId === numericId) {
				const cachedPhotoUrl = this.currentUserService.getProfilePhotoUrl();
				if (cachedPhotoUrl !== undefined) {
					this.fullImageUrl = cachedPhotoUrl || undefined;
					this.loadOriginalImage();
					this.cdr.markForCheck();
					return;
				}
			}

			// 1. Check if in cache and valid
			const cachedEntry = AvatarComponent.photoCache.get(numericId);
			if (cachedEntry) {
				const isValid = AvatarComponent.isCacheValid(cachedEntry);

				if (isValid) {
					this.fullImageUrl = cachedEntry.url || undefined;
					// Load original image for preview
					this.loadOriginalImage();
					this.cdr.markForCheck();
					return;
				} else {
					// Remove expired cache entry
					AvatarComponent.photoCache.delete(numericId);
				}
			}

			// 2. Check if there's a request in progress
			const pendingRequest = AvatarComponent.pendingRequests.get(numericId);
			if (pendingRequest) {
				pendingRequest.subscribe({
					next: (response) => {
						this.fullImageUrl = response.url || undefined;
						// Load original image for preview
						this.loadOriginalImage();
						this.cdr.markForCheck();
					},
					error: (err) => {
						console.error(`❌ Error in pending request for user ${numericId}:`, err);
						this.fullImageUrl = undefined;
						this.cdr.markForCheck();
					},
				});
				return;
			}

			// 3. Create new request and cache it
			const request$ = this.http
				.get<{ url: string | null }>(`${environment.pathBackend}/users/${numericId}/profile-photo`)
				.pipe(
					tap((response) => {
						// Save to cache
						const cacheEntry = {
							url: response.url,
							timestamp: Date.now(),
						};
						AvatarComponent.photoCache.set(numericId, cacheEntry);
						// Clear pending request
						AvatarComponent.pendingRequests.delete(numericId);
					}),
					shareReplay(1), // Share result between multiple subscribers
				);

			// Save as pending request
			AvatarComponent.pendingRequests.set(numericId, request$);

			// Subscribe to update component
			request$.subscribe({
				next: (response) => {
					this.fullImageUrl = response.url || undefined;
					// Load original image for preview
					this.loadOriginalImage();
					this.cdr.markForCheck();
				},
				error: (err) => {
					// Only log errors that are not 404 (missing photo is expected)
					if (err?.status !== 404) {
						console.error(`❌ Error fetching profile photo for user ${numericId}:`, err);
					}
					this.fullImageUrl = undefined;
					// Save error in cache to avoid immediate retries
					const errorCacheEntry = {
						url: null,
						timestamp: Date.now(),
					};
					AvatarComponent.photoCache.set(numericId, errorCacheEntry);
					// Clear pending request
					AvatarComponent.pendingRequests.delete(numericId);
					this.cdr.markForCheck();
				},
			});
		}
		// If it's a full URL (http/https), use it directly
		else if (this.imageUrl?.startsWith('http')) {
			this.fullImageUrl = this.imageUrl;
			this.originalImageUrl = this.imageUrl; // For direct URLs, use same URL
			this.cdr.markForCheck();
		}
		// If imageUrl is null or undefined, no image (initials will be shown)
		else {
			this.fullImageUrl = undefined;
			this.cdr.markForCheck();
		}
	}

	getInitials(name: string): string {
		return name
			.split(' ')
			.map((word) => word[0])
			.join('')
			.toUpperCase()
			.slice(0, 2);
	}
}
